import {
  Button,
  Center,
  Divider,
  Group,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconArrowsSort, IconMoodCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

import type { RefreshInterval } from '../api/queries';
import { useMandatoryBurden, useMandatoryPayments } from '../api/queries';
import type {
  IntervalUnit,
  MandatoryPayment,
  MandatoryPaymentSort,
  Periodicity,
} from '../api/types';
import { formatDate, formatMoney, today } from '../lib/format';

/** The whole list is shown at once; beyond this it is cut off with a hint. */
export const MANDATORY_PAYMENTS_LIMIT = 50;

const SKELETON_ROWS = 3;

const SORT_OPTIONS: { value: MandatoryPaymentSort; label: string }[] = [
  { value: 'next_due_on', label: 'По дате следующего платежа' },
  { value: 'amount', label: 'По сумме платежа' },
];

const PERIODICITY_LABELS: Record<Periodicity, string> = {
  monthly: 'раз в месяц',
  quarterly: 'раз в квартал',
  semiannual: 'раз в полгода',
  yearly: 'раз в год',
};

const INTERVAL_UNIT_LABELS: Record<IntervalUnit, [string, string]> = {
  day: ['день', 'дн.'],
  week: ['неделю', 'нед.'],
  month: ['месяц', 'мес.'],
  year: ['год', 'г.'],
};

function recurrenceLabel(payment: MandatoryPayment): string {
  if (payment.periodicity !== null) return PERIODICITY_LABELS[payment.periodicity];
  const [one, many] = INTERVAL_UNIT_LABELS[payment.interval_unit];
  return payment.interval === 1 ? `раз в ${one}` : `каждые ${payment.interval} ${many}`;
}

/**
 * What has to be paid over and over: rent, коммуналка, подписки. Read-only like
 * the rest of the UI — обязательные платежи заводятся через MCP.
 *
 * По умолчанию сверху ближайшие платежи; порядок можно переключить на сумму
 * одного платежа, чтобы увидеть самые крупные списания.
 *
 * Сумма внизу приходит с сервера отдельным запросом, а не складывается из строк
 * на экране: она считается по всем платежам, включая те, что в список не влезли.
 */
export function MandatoryPaymentsPanel({
  refreshInterval,
  onCountChange,
}: {
  refreshInterval: RefreshInterval;
  onCountChange?: (count: number | null) => void;
}) {
  const [sort, setSort] = useState<MandatoryPaymentSort>('next_due_on');
  const { data, isPending, error, refetch } = useMandatoryPayments(
    MANDATORY_PAYMENTS_LIMIT,
    sort,
    refreshInterval,
  );
  const { data: burden } = useMandatoryBurden(refreshInterval);

  const payments = data?.payments ?? [];

  useEffect(() => {
    onCountChange?.(error !== null || isPending ? null : payments.length);
  }, [error, isPending, onCountChange, payments.length]);

  return (
    <Stack gap="md">
      {!error && !isPending && payments.length > 0 && (
        <Select
          size="xs"
          aria-label="Сортировка обязательных платежей"
          allowDeselect={false}
          checkIconPosition="right"
          leftSection={<IconArrowsSort size={14} stroke={1.5} />}
          value={sort}
          onChange={(value) => {
            if (value !== null) setSort(value as MandatoryPaymentSort);
          }}
          data={SORT_OPTIONS}
        />
      )}

      {error ? (
        <ErrorState
          message={error.message}
          onRetry={() => {
            void refetch();
          }}
        />
      ) : isPending ? (
        <Stack gap="sm">
          {Array.from({ length: SKELETON_ROWS }, (_, index) => (
            <Skeleton key={index} height={44} radius="sm" />
          ))}
        </Stack>
      ) : payments.length === 0 ? (
        <EmptyState />
      ) : (
        <PaymentsTable payments={payments} />
      )}

      {!error && !isPending && burden && burden.overdue_amount > 0 && (
        <Text size="sm" c="red" fw={600}>
          Всего просрочено: {formatMoney(burden.overdue_amount)}
        </Text>
      )}
      {!error && !isPending && payments.length > 0 && (
        <MonthlyTotal
          monthly={burden?.monthly ?? null}
          yearly={burden?.yearly ?? null}
          truncated={data?.hasMore ?? false}
        />
      )}
    </Stack>
  );
}

function PaymentsTable({ payments }: { payments: MandatoryPayment[] }) {
  return (
    <Table.ScrollContainer minWidth={320}>
      <Table verticalSpacing="xs" horizontalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                Платёж
              </Text>
            </Table.Th>
            <Table.Th ta="right">
              <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                В месяц
              </Text>
            </Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {payments.map((payment) => (
            <PaymentRow key={payment.id} payment={payment} />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

function PaymentRow({ payment }: { payment: MandatoryPayment }) {
  return (
    <Table.Tr>
      <Table.Td>
        <Stack gap={2} style={{ minWidth: 0 }}>
          <Text size="sm" fw={500} lineClamp={1}>
            {payment.title}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {formatMoney(payment.amount)} {recurrenceLabel(payment)}
          </Text>
        </Stack>
      </Table.Td>

      <Table.Td ta="right">
        <Stack gap={2} align="flex-end" style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(payment.monthly)}
          </Text>
          <NextPayment payment={payment} />
          {payment.is_overdue && (
            <Text size="xs" c="red">
              Долг: {formatMoney(payment.overdue_amount)} · {payment.overdue_count} плат.
            </Text>
          )}
        </Stack>
      </Table.Td>
    </Table.Tr>
  );
}

/** Ближайшее незакрытое вхождение может быть просрочено. */
function NextPayment({ payment }: { payment: MandatoryPayment }) {
  if (payment.next_due_on === null) {
    return <Text size="xs" c="dimmed">Нет предстоящих начислений</Text>;
  }
  const soon = payment.next_due_on === today();
  const schedule = payment.next_scheduled_on === null
    ? 'Новых начислений нет'
    : `По расписанию: ${formatDate(payment.next_scheduled_on)}`;

  return (
    // Слева, а не сверху: сверху подсказка накрывает ту самую сумму, рядом с
    // которой её открыли.
    <Tooltip label={`${schedule} · В год: ${formatMoney(payment.yearly)}`} position="left" withArrow>
      <Text size="xs" c={payment.is_overdue ? 'red' : soon ? 'orange' : 'dimmed'}>
        {payment.is_overdue ? `просрочен с ${formatDate(payment.next_due_on)}` : soon ? 'сегодня' : `до ${formatDate(payment.next_due_on)}`}
      </Text>
    </Tooltip>
  );
}

function MonthlyTotal({
  monthly,
  yearly,
  truncated,
}: {
  monthly: number | null;
  yearly: number | null;
  truncated: boolean;
}) {
  return (
    <>
      <Divider />
      <Group justify="space-between" wrap="nowrap">
        <Stack gap={0}>
          <Text size="xs" c="dimmed">
            Итого в месяц
          </Text>
          {truncated && (
            <Text size="xs" c="dimmed">
              по всем платежам, включая не показанные
            </Text>
          )}
        </Stack>
        <Stack gap={0} align="flex-end" style={{ whiteSpace: 'nowrap' }}>
          <Text size="sm" fw={600} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {monthly === null ? '—' : formatMoney(monthly)}
          </Text>
          {yearly !== null && (
            <Text size="xs" c="dimmed">
              {formatMoney(yearly)} в год
            </Text>
          )}
        </Stack>
      </Group>
    </>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Center py={24}>
      <Stack gap="xs" align="center" maw={280}>
        <ThemeIcon variant="light" color="red" size="lg" radius="md">
          <IconAlertTriangle size={18} stroke={1.5} />
        </ThemeIcon>
        <Text size="sm" fw={600}>
          Не удалось загрузить платежи
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          {message}
        </Text>
        <Button size="xs" variant="default" mt={4} onClick={onRetry}>
          Повторить
        </Button>
      </Stack>
    </Center>
  );
}

function EmptyState() {
  return (
    <Center py={32}>
      <Stack gap="xs" align="center" maw={240}>
        <IconMoodCheck size={26} stroke={1.5} opacity={0.5} />
        <Text size="sm" c="dimmed" ta="center">
          Обязательных платежей нет
        </Text>
        <Text size="xs" c="dimmed" ta="center">
          Завести регулярный платёж можно через MCP
        </Text>
      </Stack>
    </Center>
  );
}
